import { z } from 'zod'

/** What an event or place is good for; an item can fit several. */
export const ITEM_OCCASIONS = [
  'party',
  'date',
  'coffee',
  'business',
  'family',
  'friends',
  'culture',
  'sport',
  'food',
  'students',
  'outdoor',
  'study',
] as const
export type ItemOccasion = (typeof ITEM_OCCASIONS)[number]

export const ITEM_MOODS = ['calm', 'social', 'lively'] as const
export const ITEM_SETTINGS = ['indoor', 'outdoor', 'both'] as const
export const ITEM_PRICE_LEVELS = ['low', 'mid', 'high', 'unknown'] as const
export const ITEM_AUDIENCES = ['all', 'adults'] as const

/** The times of day an item is for or happens; night is after about 22:00. */
export const ITEM_TIMES = ['morning', 'day', 'evening', 'night'] as const
export type ItemTime = (typeof ITEM_TIMES)[number]

/** What kind of event an event is; places have none. */
export const ITEM_EVENT_TYPES = [
  'concert',
  'club_night',
  'student_party',
  'festival',
  'theatre',
  'exhibition',
  'talk_networking',
  'workshop_course',
  'sport',
  'market',
  'kids',
  'food_drink',
  'other',
] as const
export type ItemEventType = (typeof ITEM_EVENT_TYPES)[number]

const unit = z.number().min(0).max(1)

/**
 * How an event or place is sorted: for each occasion how well it fits (0 to 1), and one value per
 * property with how sure the sorting is about it (0 to 1). `quality` says how real, specific and
 * worth showing the entry is. The fields from `times` on came with the second sorting and are
 * optional, so sortings stored before them still read.
 */
export const itemSortingSchema = z.object({
  occasions: z.record(z.enum(ITEM_OCCASIONS), unit),
  mood: z.enum(ITEM_MOODS),
  setting: z.enum(ITEM_SETTINGS),
  priceLevel: z.enum(ITEM_PRICE_LEVELS),
  audience: z.enum(ITEM_AUDIENCES),
  times: z.array(z.enum(ITEM_TIMES)).max(ITEM_TIMES.length).optional(),
  eventType: z.enum(ITEM_EVENT_TYPES).nullable().optional(),
  quality: unit.optional(),
  englishFriendly: z.boolean().optional(),
  studentDeal: z.boolean().optional(),
  confidence: z.object({
    mood: unit,
    setting: unit,
    priceLevel: unit,
    audience: unit,
    times: unit.optional(),
    eventType: unit.optional(),
    englishFriendly: unit.optional(),
    studentDeal: unit.optional(),
  }),
})
export type ItemSorting = z.infer<typeof itemSortingSchema>
